// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { clearAuth, getToken } from "./utils/auth";
import api from "./utils/api";
import "./utils/pwaInstallPrompt";
import {
  applyLanguagePreference,
  clearLandingLanguagePreference,
  readLandingLanguagePreference,
} from "./utils/regionalPreferences";

import AuthLandingPage from "./pages/AuthLandingPage";

const RetailerMap = React.lazy(() => import("./components/RetailerMap"));
const SuccessPageStripe = React.lazy(() => import("./components/SuccessPageStripe"));
const CancelPageStripe = React.lazy(() => import("./components/CancelPageStripe"));
const StripeTest = React.lazy(() => import("./components/StripeTest"));
const MyInterestsPage = React.lazy(() => import("./pages/MyInterestsPage"));
const AanvragenPage = React.lazy(() => import("./pages/AanvragenPage"));
const AccountSettingsPage = React.lazy(() => import("./pages/AccountSettingsPage"));
const BrokerPropertiesPage = React.lazy(() => import("./pages/BrokerPropertiesPage"));
const MyPropertiesPage = React.lazy(() => import("./pages/MyPropertiesPage"));
const PropertyDetailsPage = React.lazy(() => import("./pages/PropertyDetailsPage"));
const OwnerListingCheckout = React.lazy(() => import("./pages/OwnerListingCheckout"));
const ContactSuccess = React.lazy(() => import("./pages/ContactSuccess"));
const OwnerListingsPage = React.lazy(() => import("./pages/OwnerListingsPage"));
const RetailerListingsPage = React.lazy(() => import("./pages/RetailerListingsPage"));
const RetailerPropertiesPage = React.lazy(() => import("./pages/RetailerPropertiesPage"));
const MyLikesPage = React.lazy(() => import("./pages/MyLikesPage"));
const DashboardHome = React.lazy(() => import("./pages/DashboardHome"));
const RetailerPropertyDetailPage = React.lazy(() => import("./pages/RetailerPropertyDetailPage"));
const RetailerPropertyOverlay = React.lazy(() => import("./overlays/RetailerPropertyOverlay"));
const BrandQueueDashboard = React.lazy(() => import("./pages/Admin/BrandQueueDashboard"));
const EmailAgentDashboard = React.lazy(() => import("./pages/Admin/EmailAgentDashboard"));
const MailPreviewDashboard = React.lazy(() => import("./pages/Admin/MailPreviewDashboard"));
const AdminHelpAssistantLab = React.lazy(() => import("./pages/Admin/AdminHelpAssistantLab"));
const AdminHomePage = React.lazy(() => import("./pages/Admin/AdminHomePage"));
const AdminUsersPage = React.lazy(() => import("./pages/Admin/AdminUsersPage"));
const AdminStatisticsDashboard = React.lazy(() => import("./pages/Admin/AdminStatisticsDashboard"));
const AdminAdministrationDashboard = React.lazy(() => import("./pages/Admin/AdminAdministrationDashboard"));
const IndoorMapDashboard = React.lazy(() => import("./pages/Admin/IndoorMapDashboard"));
const AdminRetailPrefillDashboard = React.lazy(() => import("./pages/Admin/AdminRetailPrefillDashboard"));
const AdminRetailerLocationIntelligencePage = React.lazy(() => import("./pages/Admin/AdminRetailerLocationIntelligencePage"));
const AdminExternalListingsPage = React.lazy(() => import("./pages/Admin/AdminExternalListingsPage"));
const MapSnapshot = React.lazy(() => import("./components/MapSnapshot"));
const ConsentBanner = React.lazy(() => import("./components/ConsentBanner"));
const AnalyticsRuntime = React.lazy(() => import("./components/analytics/AnalyticsRuntime"));
const LegalPage = React.lazy(() => import("./pages/LegalPage"));
const DataSecurityPage = React.lazy(() => import("./pages/DataSecurityPage"));
const OverlayStack = React.lazy(() => import("./components/OverlayStack"));
const DashboardOverlay = React.lazy(() => import("./overlays/DashboardOverlay"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = React.lazy(() => import("./pages/VerifyEmailPage"));


// =======================================
// 🔐 ProtectedRoute
// =======================================
const ProtectedRoute = ({ children }) => {
  const token = getToken();

  if (!token) return <Navigate to="/login" replace />;

  try {
    const [, payloadBase64] = token.split(".");
    const payload = JSON.parse(atob(payloadBase64));

    if (!payload.exp || Date.now() > payload.exp * 1000) {
      clearAuth();
      return <Navigate to="/login" replace />;
    }

    return children;
  } catch (err) {
    clearAuth();
    return <Navigate to="/login" replace />;
  }
};

const PublicEntryRoute = ({ mode }) => <AuthLandingPage initialMode={mode} />;

const AUTH_LANDING_PATHS = new Set(["/", "/login", "/register", "/forgot-password"]);

const AuthLandingRoutes = () => (
  <Routes>
    <Route path="/" element={<PublicEntryRoute mode="register" />} />
    <Route path="/login" element={<PublicEntryRoute mode="login" />} />
    <Route path="/register" element={<PublicEntryRoute mode="register" />} />
    <Route path="/forgot-password" element={<PublicEntryRoute mode="forgot" />} />
  </Routes>
);

// Development-only preview; no change to the live consent flow or analytics activation.
const AnalyticsConsentPreview = process.env.NODE_ENV === 'development'
  ? React.lazy(() => import('./components/analytics/AnalyticsConsentPreview'))
  : null;

const AccountLanguageSync = () => {
  const location = useLocation();
  const lastAppliedTokenRef = React.useRef("");

  React.useEffect(() => {
    const token = getToken();
    if (!token || lastAppliedTokenRef.current === token) return;

    let cancelled = false;

    const applyAccountLanguage = async () => {
      try {
        const res = await api.get("/api/account-settings/regional-preferences");
        if (cancelled) return;
        const landingLanguage = readLandingLanguagePreference();
        const preferences = {
          language: landingLanguage || res.data?.language || "auto",
          currency: res.data?.currency || "auto",
          units: res.data?.units || "auto",
        };

        await applyLanguagePreference(preferences);

        if (landingLanguage && landingLanguage !== res.data?.language) {
          await api.put("/api/account-settings/regional-preferences", preferences);
        }
        if (landingLanguage) clearLandingLanguagePreference();
        lastAppliedTokenRef.current = token;
      } catch (err) {
        console.warn("Accounttaal laden mislukt:", err?.message || err);
      }
    };

    applyAccountLanguage();

    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  return null;
};


// =======================================
// 🛡️ AdminRoute
// =======================================
const AdminRoute = ({ children }) => {
  const token = getToken();
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchUser = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get("/api/user");
        setUser(res.data);
      } catch (err) {
        console.warn("❌ Kon gebruiker niet ophalen:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [token]);

  if (loading) return null;
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/map" replace />;

  return children;
};


// =======================================
// 🚀 APP
// =======================================
const App = () => {
  return (
    <Router>
      <React.Suspense fallback={null}>
        <AnalyticsRuntime />
        <AppRoutes />
      </React.Suspense>
    </Router>
  );
};

const AppRoutes = () => {
  const location = useLocation();

  if (AnalyticsConsentPreview && location.pathname === '/__preview/analytics-consent') {
    return <React.Suspense fallback={null}><AnalyticsConsentPreview /></React.Suspense>;
  }

  if (AUTH_LANDING_PATHS.has(location.pathname)) {
    return <React.Suspense fallback={null}>
      <ConsentBanner />
      <AuthLandingRoutes />
    </React.Suspense>;
  }

  return (
    <React.Suspense fallback={null}>
      <AccountLanguageSync />
      <ConsentBanner />
      <OverlayStack>
        <Routes>

          {/* ===============================
              🔓 Publieke routes
          =============================== */}
          <Route path="/" element={<PublicEntryRoute mode="register" />} />
          <Route path="/login" element={<PublicEntryRoute mode="login" />} />
          <Route path="/register" element={<PublicEntryRoute mode="register" />} />
          <Route path="/forgot-password" element={<PublicEntryRoute mode="forgot" />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/privacy" element={<LegalPage type="privacy" />} />
          <Route path="/voorwaarden" element={<LegalPage type="voorwaarden" />} />
          <Route path="/data-veiligheid" element={<DataSecurityPage />} />
          <Route path="/data-security" element={<DataSecurityPage />} />

          {/* Stripe publiek */}
          <Route path="/success" element={<SuccessPageStripe />} />
          <Route path="/cancel" element={<CancelPageStripe />} />
          <Route path="/stripe/success" element={<SuccessPageStripe />} />
          <Route path="/stripe/cancel" element={<CancelPageStripe />} />
          <Route path="/cancel-retailer" element={<CancelPageStripe />} />
          <Route path="/stripe-test" element={<StripeTest />} />
          <Route path="/contact/success" element={<ContactSuccess />} />
          <Route path="/stripe-success" element={<SuccessPageStripe />} />

          {/* ===============================
              🔐 Alles hieronder vereist login
          =============================== */}
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <>
                  {/* Kaart altijd zichtbaar na login */}
                  <RetailerMap />

                  <Routes>
                    <Route path="/map" element={null} />

                    {/* Dashboard */}
                    <Route
                      path="/dashboard/*"
                      element={<DashboardOverlay />}
                    >
                      <Route index element={<DashboardHome />} />
                      <Route path="settings" element={<AccountSettingsPage />} />
                      <Route path="profile" element={<Navigate to="/dashboard/settings?tab=profile" replace />} />
                      <Route path="interests" element={<MyInterestsPage />} />
                      <Route path="likes" element={<MyLikesPage />} />
                      <Route path="interesses" element={<Navigate to="/dashboard/interests" replace />} />
                      <Route path="aanvragen" element={<AanvragenPage />} />
                      <Route path="portefeuille" element={<MyPropertiesPage />} />
                      <Route path="account" element={<Navigate to="/dashboard/settings?tab=profile" replace />} />
                      <Route path="panden" element={<BrokerPropertiesPage />} />
                      <Route path="retailer-panden" element={<RetailerPropertiesPage />} />
                      <Route path="verhuuraanbod" element={<OwnerListingsPage />} />
                      <Route path="tehuur" element={<RetailerListingsPage />} />
                    </Route>

                    {/* Extra aanvragen route (losse) */}
                    <Route path="/aanvragen" element={<AanvragenPage />} />

                    {/* Owner checkout */}
                    <Route path="/owner-listing/checkout" element={<OwnerListingCheckout />} />

                    {/* Detail routes */}
                    <Route path="/property/:id" element={<PropertyDetailsPage />} />
                    <Route
  path="/property/:id/tehuur"
  element={<PropertyDetailsPage defaultTab="tehuur" />}
/>
                    <Route path="/retailer-property/:id" element={<RetailerPropertyDetailPage />} />
                    <Route path="/retailer-property-overlay/:id" element={<RetailerPropertyOverlay />} />
                    <Route path="/map-snapshot" element={<MapSnapshot />} />

                    {/* Admin */}
                    <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminHomePage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/email-agent"
                      element={
                        <AdminRoute>
                          <EmailAgentDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/mail-previews"
                      element={
                        <AdminRoute>
                          <MailPreviewDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/brand-queue"
                      element={
                        <AdminRoute>
                          <BrandQueueDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/help-assistant"
                      element={
                        <AdminRoute>
                          <AdminHelpAssistantLab />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/statistics"
                      element={
                        <AdminRoute>
                          <AdminStatisticsDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/administration"
                      element={
                        <AdminRoute>
                          <AdminAdministrationDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/indoor-maps"
                      element={
                        <AdminRoute>
                          <IndoorMapDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/retail-prefill"
                      element={
                        <AdminRoute>
                          <AdminRetailPrefillDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/retailer-location-intelligence"
                      element={
                        <AdminRoute>
                          <AdminRetailerLocationIntelligencePage />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/highlight-correctie"
                      element={
                        <AdminRoute>
                          {null}
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/external-listings"
                      element={
                        <AdminRoute>
                          <AdminExternalListingsPage />
                        </AdminRoute>
                      }
                    />

                  </Routes>
                </>
              </ProtectedRoute>
            }
          />

        </Routes>
      </OverlayStack>
    </React.Suspense>
  );
};

export default App;
